import { useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Phone, Send, CheckCircle2, Sparkles, Compass, LifeBuoy,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const SUPPORT_EMAIL = "info@incassi.cloud";

// Le opzioni del checkbox onboarding sono in un solo posto per non duplicare.
const ONBOARDING_AREAS = [
  { id: "smtp", label: "SMTP email (invio riepiloghi con il tuo dominio)" },
  { id: "collaboratori", label: "Collaboratori e utenze" },
  { id: "pos", label: "Metodi di pagamento (POS)" },
  { id: "branding", label: "Logo e branding agenzia" },
  { id: "plurimandatari", label: "Plurimandatari (multi-compagnia)" },
  { id: "stripe", label: "Pagamenti e abbonamento" },
];

// Configurazione delle 3 varianti (demo landing / onboarding / assistenza).
// Il variant è derivato automaticamente dalla prop `source` per compatibilità.
const VARIANTS = {
  demo: {
    icon: Sparkles,
    iconClass: "text-blue-600",
    title: "Prenota una demo gratuita",
    description: "Ti mostriamo Incassi.cloud in azione in circa 20 minuti, in call video. Nessun impegno.",
    submitLabel: "Prenota demo",
    successTitle: "Prenotazione ricevuta!",
    successBody: "Ti scriviamo entro 24 ore lavorative per confermare data e orario.",
    notePlaceholder: "Es. Siamo un'agenzia con 6 collaboratori, vorremmo capire come funziona il modulo POS",
    testidSuffix: "demo",
  },
  onboarding: {
    icon: Compass,
    iconClass: "text-emerald-600",
    title: "Ti aiutiamo con la prima configurazione",
    description: "Scegli i punti su cui vuoi essere seguito: ti ricontattiamo entro 24 ore lavorative per configurare tutto insieme.",
    submitLabel: "Richiedi call onboarding",
    successTitle: "Ottimo! Ci sentiamo presto.",
    successBody: "Ti chiamiamo entro 24 ore lavorative per completare insieme la configurazione.",
    notePlaceholder: "Es. Ho SMTP Aruba, 4 collaboratori, uso POS Nexi con split copia unica",
    testidSuffix: "onboarding",
  },
  assistance: {
    icon: LifeBuoy,
    iconClass: "text-amber-600",
    title: "Richiedi assistenza",
    description: "Descrivici brevemente il problema. Rispondiamo entro 24 ore lavorative via email.",
    submitLabel: "Invia richiesta",
    successTitle: "Richiesta inviata!",
    successBody: "Ti risponderemo al più presto via email. Per urgenze scrivi direttamente al supporto.",
    notePlaceholder: "Es. Nel riepilogo PDF di ieri gli importi non tornano rispetto al versamento inserito",
    testidSuffix: "assistance",
  },
};

const ARGOMENTI = [
  "Incassi e foglio cassa",
  "Versamenti",
  "Report PDF/Excel",
  "Email e notifiche",
  "Pagamenti e abbonamento",
  "Altro",
];
const URGENZE = ["Bassa", "Media", "Alta"];
const NUMERO_COLLAB_OPTS = ["1 (solo io)", "2-3", "4-7", "8-15", "Più di 15"];

function sourceToVariant(source) {
  if (source === "aiuto") return "assistance";
  if (source === "onboarding") return "onboarding";
  return "demo"; // "landing" e default
}

/**
 * RequestCallDialog — modale differenziato per contesto:
 * - variant "demo"        → Prenotazione demo (dalla landing, prospect non loggato)
 * - variant "onboarding"  → Prima configurazione (banner post-signup nell'AdminLayout)
 * - variant "assistance"  → Richiesta assistenza (pagina Aiuto per utenti già attivi)
 *
 * Props:
 *  - open, onOpenChange: controllo del Dialog
 *  - source: "landing" | "aiuto" | "onboarding"  (inviato al backend, deriva la variante)
 *  - variant: override esplicito ("demo" | "onboarding" | "assistance"); se omesso, derivato da `source`
 *  - defaults: { nome, email, agenzia } per pre-compilare quando l'utente è loggato
 *  - onSuccess: callback dopo invio riuscito (es. per marcare onboarding=requested)
 */
export default function RequestCallDialog({
  open,
  onOpenChange,
  source = "landing",
  variant,
  defaults = {},
  onSuccess,
}) {
  const variantKey = variant || sourceToVariant(source);
  const cfg = VARIANTS[variantKey] || VARIANTS.demo;
  const Icon = cfg.icon;

  const [form, setForm] = useState({
    nome: defaults.nome || "",
    email: defaults.email || "",
    telefono: "",
    agenzia: defaults.agenzia || "",
    fascia_oraria: "",
    note: "",
    // Campi extra per variante (inviati solo se popolati)
    argomento: "",
    urgenza: "",
    numero_collaboratori: "",
    come_ci_hai_conosciuto: "",
    configurazioni_richieste: [],
  });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setValue = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleArea = (id) => setForm((f) => {
    const cur = f.configurazioni_richieste || [];
    return { ...f, configurazioni_richieste: cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id] };
  });

  const payload = useMemo(() => ({
    nome: form.nome,
    email: form.email,
    telefono: form.telefono,
    agenzia: form.agenzia,
    fascia_oraria: form.fascia_oraria,
    note: form.note,
    source,
    argomento: variantKey === "assistance" ? form.argomento : "",
    urgenza: variantKey === "assistance" ? form.urgenza : "",
    numero_collaboratori: variantKey === "demo" ? form.numero_collaboratori : "",
    come_ci_hai_conosciuto: variantKey === "demo" ? form.come_ci_hai_conosciuto : "",
    configurazioni_richieste: variantKey === "onboarding"
      ? (form.configurazioni_richieste || []).map((id) => ONBOARDING_AREAS.find((a) => a.id === id)?.label || id)
      : [],
  }), [form, source, variantKey]);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!form.nome.trim() || !form.email.trim()) {
      toast.error("Inserisci almeno nome e email");
      return;
    }
    setSending(true);
    try {
      await api.post("/support/request-call", payload);
      setDone(true);
      toast.success("Richiesta inviata. Ti contatteremo a breve.");
      onSuccess?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore invio richiesta");
    } finally {
      setSending(false);
    }
  };

  const close = () => {
    onOpenChange?.(false);
    // Reset after dialog closes
    setTimeout(() => setDone(false), 300);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[92vh] overflow-y-auto" data-testid={`request-call-dialog-${cfg.testidSuffix}`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Icon className={`w-5 h-5 ${cfg.iconClass}`} />
            {cfg.title}
          </DialogTitle>
          <DialogDescription>
            {cfg.description}{" "}
            <a className="text-blue-600 underline" href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
          </DialogDescription>
        </DialogHeader>

        {done ? (
          <div className="py-8 text-center" data-testid="request-call-success">
            <CheckCircle2 className="w-12 h-12 text-emerald-600 mx-auto" />
            <h3 className="font-heading font-semibold text-lg mt-3">{cfg.successTitle}</h3>
            <p className="text-sm text-slate-600 mt-2">
              {cfg.successBody}{" "}
              <a className="text-blue-600 underline" href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
            </p>
            <Button onClick={close} className="mt-5" data-testid="request-call-close">Chiudi</Button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">Nome e cognome *</Label>
                <Input value={form.nome} onChange={set("nome")} placeholder="Mario Rossi" data-testid="rc-nome" required />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">Email *</Label>
                <Input type="email" value={form.email} onChange={set("email")} placeholder="mario@studio.it" data-testid="rc-email" required />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">Telefono</Label>
                <Input value={form.telefono} onChange={set("telefono")} placeholder="+39 ..." data-testid="rc-telefono" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">Agenzia</Label>
                <Input value={form.agenzia} onChange={set("agenzia")} placeholder="Es. Rossi Assicurazioni" data-testid="rc-agenzia" />
              </div>
            </div>

            {/* === Campi contestuali === */}
            {variantKey === "demo" && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-500">Numero collaboratori</Label>
                  <Select value={form.numero_collaboratori} onValueChange={setValue("numero_collaboratori")}>
                    <SelectTrigger data-testid="rc-numero-collab"><SelectValue placeholder="Seleziona…" /></SelectTrigger>
                    <SelectContent>
                      {NUMERO_COLLAB_OPTS.map((o) => (<SelectItem key={o} value={o}>{o}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-500">Come ci hai conosciuto</Label>
                  <Input value={form.come_ci_hai_conosciuto} onChange={set("come_ci_hai_conosciuto")} placeholder="Es. Google, collega, LinkedIn" data-testid="rc-canale" />
                </div>
              </div>
            )}

            {variantKey === "assistance" && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-500">Argomento</Label>
                  <Select value={form.argomento} onValueChange={setValue("argomento")}>
                    <SelectTrigger data-testid="rc-argomento"><SelectValue placeholder="Seleziona…" /></SelectTrigger>
                    <SelectContent>
                      {ARGOMENTI.map((o) => (<SelectItem key={o} value={o}>{o}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-slate-500">Urgenza</Label>
                  <Select value={form.urgenza} onValueChange={setValue("urgenza")}>
                    <SelectTrigger data-testid="rc-urgenza"><SelectValue placeholder="Media" /></SelectTrigger>
                    <SelectContent>
                      {URGENZE.map((o) => (<SelectItem key={o} value={o}>{o}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {variantKey === "onboarding" && (
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">Aree da configurare</Label>
                <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {ONBOARDING_AREAS.map((a) => {
                    const checked = form.configurazioni_richieste.includes(a.id);
                    return (
                      <label
                        key={a.id}
                        className={`flex items-start gap-2 rounded-md border px-3 py-2 cursor-pointer text-sm transition ${checked ? "border-emerald-500 bg-emerald-50" : "border-slate-200 hover:bg-slate-50"}`}
                        data-testid={`rc-area-${a.id}`}
                      >
                        <Checkbox checked={checked} onCheckedChange={() => toggleArea(a.id)} className="mt-0.5" />
                        <span className="text-slate-700">{a.label}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}

            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Fascia oraria preferita</Label>
              <Input value={form.fascia_oraria} onChange={set("fascia_oraria")} placeholder="Es. Mar–Gio 14:00–17:00" data-testid="rc-fascia" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">
                {variantKey === "assistance" ? "Descrivi il problema" : "Note"}
              </Label>
              <Textarea rows={3} value={form.note} onChange={set("note")} placeholder={cfg.notePlaceholder} data-testid="rc-note" />
            </div>
            <DialogFooter className="gap-2 sm:gap-2">
              <Button type="button" variant="outline" onClick={close} data-testid="rc-cancel">Annulla</Button>
              <Button type="submit" disabled={sending} data-testid="rc-submit">
                <Send className="w-4 h-4 mr-2" />
                {sending ? "Invio..." : cfg.submitLabel}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

export { SUPPORT_EMAIL };
